import * as React from "react";
import { getOrder, OrderedSku } from "../../domain/Order";
import ProductModel, { OrderItemForExcel } from "../../domain/Product";
import OrderDetails from "./OrderDetails";
import isLiabilityMet, { LiabilityMet } from "../../domain/isLiabilityMet";
import TableHeader from "../TableHeader";
import {
  default as DispatchProps,
  mapDispatchToProps,
} from "../../application/core/DispatchProps";
import { connect } from "react-redux";
import ApplicationState from "../../application/core/State";
import headers from "./headers";
import LiabilitySlider from "./LiabilitySlider";
import OrderDetailsHeader from "./OrderDetailsHeader";
import OrderModel from "../../domain/Order";
import { EstimationMonthSum } from "../../domain/estimations/Estimations";
import { monthByNumber } from "../admin/getEstimationMonths";
import Product from "../Product";
import Loader from "../common/Loader";
import ExportExcel from "../common/ExportExcel";
import DataExportRequested from "../../application/products/events/DataExportRequested";
import { getBackend } from "../../application/core/createStore";
import api from "../../application/core/api";
import { catchError } from "rxjs/operators";
import DataExportFailed from "../../application/products/events/DataExportFailed";
import { of } from "rxjs";
import { AjaxResponse } from "rxjs/ajax";
import triggerClickOnExcelDownload from "../triggerClickOnExcelDownload";
import DataExportSucceeded from "../../application/products/events/DataExportSucceeded";
import { Column } from "../ExportDataToExcel";
import createDataForExcel from "./createDataForExcel";
import ImportExcel from "../common/ImportExcel";
import {
  OrderColumn,
  orderColumnsNeededForImport,
  orderExcelColumn,
} from "../common/excelColumns";
import OrderImportRequested from "../../application/order/events/OrderImportRequested";
import removeRowsWithoutOrder, { RowType } from "./removeRowsWithoutOrder";
import extractOnlyNeededColumns from "../common/extractOnlyNeededColumns";
import ProductsNavigation from "../common/ProductsNavigation";
import {
  getOrderNextCursor,
  getOrderPreviousCursor,
} from "../../application/products/selector";
import OrderProductsNextRequested from "../../application/products/events/OrderProductsNextRequested";
import OrderProductsPrevRequested from "../../application/products/events/OrderProductsPrevRequested";

interface StateProps {
  order?: OrderModel;
  savedOrder?: OrderModel;
  products: ProductModel[];
  estimations: EstimationMonthSum;
  orderedSku: OrderedSku;
  userLiability: number;
  discountRate: number;
  liabilityMet: LiabilityMet;
  productsLoader: boolean;
  nextCursor?: string;
  prevCursor?: string;
  cursorIndex: number;
}

interface State {
  spreadsheetData: OrderItemForExcel[];
}

class OrderList extends React.PureComponent<StateProps & DispatchProps, State> {
  constructor(props: DispatchProps & StateProps) {
    super(props);
    this.state = {
      spreadsheetData: [],
    };
  }

  public render(): JSX.Element {
    const liabilityMet = isLiabilityMet(
      this.props.liabilityMet,
      this.props.userLiability
    );
    const spreadsheetColumns: Column[] = [
      { label: "Categories", value: "categories" },
      { label: "Season", value: "season" },
      { label: "Article name", value: "articleName" },
      { label: "Article id", value: "articleId" },
      { label: "SKU", value: "sku" },
      { label: "Variant code", value: "variantCode" },
      { label: "EAN", value: "ean" },
      { label: "EAN Pack", value: "eanPack" },
      { label: "Is Collab", value: "isCollab" },
      { label: "Collection Segment", value: "collectionSegment" },
      { label: "Assortment Segment", value: "assortmentSegment" },
      { label: "Size", value: "size" },
      { label: "Price", value: "price" },
      { label: "Package size", value: "packageSize" },
      { label: "Estimated quantity", value: "estimatedQty" },
      { label: "Ordered", value: "ordered" },
      { label: "Liability met", value: "liabilityMet" },
      {
        label: `${this.props.order!.month.year}/${
          this.props.order!.month.month
        }`,
        value: "month",
      },
      { label: "Available stock", value: "availableStock" },
      { label: "Stock order", value: "stockOrder" },
    ];
    return (
      <div className="current-order-products">
        {this.props.productsLoader && <Loader />}
        <div className="current-order-header">
          <TableHeader headers={headers} class="current-order-product-header" />
          <div className="orders-details-wrapper">
            <div className="orders-liability-wrapper">
              <div className="liability-wrapper">
                <span>0%</span>
                <LiabilitySlider />
                <span>100%</span>
              </div>
              <>
                <ImportExcel
                  products={this.props.products}
                  name="Order"
                  importExcel={this.onImport}
                />
                <ExportExcel
                  onDownloadClicked={this.onExportClicked}
                  spreadsheetData={this.state.spreadsheetData}
                  columns={spreadsheetColumns}
                  name="Order"
                />
              </>
            </div>
            <OrderDetailsHeader
              month={monthByNumber[this.props.order!.month.month]}
            />
          </div>
        </div>
        <div className="current-order-list">
          {this.props.products.map((product: ProductModel) => (
            <div className="current-order-container" key={product.articleId}>
              <Product
                product={product}
                key={product.articleId}
                userDiscount={this.props.discountRate}
              />
              <OrderDetails
                estimations={this.props.estimations}
                product={product}
                model={getOrder(
                  this.props.order!.articlesOrdersDrafts,
                  product
                )}
                savedModel={getOrder(
                  this.props.savedOrder!.articlesOrdersDrafts,
                  product
                )}
                month={monthByNumber[this.props.order!.month.month]}
                packageSize={product.packageSize}
                isLiabilityMet={liabilityMet}
                orderedSku={this.props.orderedSku}
              />
            </div>
          ))}
          <ProductsNavigation
            cursorIndex={this.props.cursorIndex}
            nextCursor={this.props.nextCursor}
            prevCursor={this.props.prevCursor}
            next={this.nextProducts}
            prev={this.prevProducts}
          />
        </div>
      </div>
    );
  }

  private onExportClicked = (): void => {
    this.props.dispatch(new DataExportRequested());
    getBackend()
      .get(`${api()}orders/current/export`)
      .pipe(
        catchError(() => {
          this.props.dispatch(new DataExportFailed());
          return of(null);
        })
      )
      .subscribe((response: AjaxResponse | null) => {
        if (response !== null) {
          const spreadsheetData: OrderItemForExcel[] = createDataForExcel(
            response.response,
            this.props.order!
          );
          this.setState({ spreadsheetData }, () => {
            triggerClickOnExcelDownload("estimations-download-button");
            this.setState({ spreadsheetData: [] });
            this.props.dispatch(new DataExportSucceeded());
          });
        }
      });
  };

  private onImport = (rows: RowType[][]): void => {
    this.props.dispatch(
      new OrderImportRequested(
        removeRowsWithoutOrder(
          extractOnlyNeededColumns<OrderColumn>(
            orderColumnsNeededForImport,
            rows,
            orderExcelColumn
          )
        )
      )
    );
  };

  private nextProducts = (): void =>
    this.props.dispatch(new OrderProductsNextRequested());
  private prevProducts = (): void =>
    this.props.dispatch(new OrderProductsPrevRequested());
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
  order: state.order.order,
  savedOrder: state.order.savedOrder,
  products: state.products.orderProducts,
  estimations: state.order.articlesEstimations,
  orderedSku: state.order.orderedSku,
  userLiability: state.user.liabilityRate,
  discountRate: state.user.discountRate,
  liabilityMet: state.order.liabilityMet,
  productsLoader: state.products.productsLoader,
  nextCursor: getOrderNextCursor(state.products),
  prevCursor: getOrderPreviousCursor(state.products),
  cursorIndex: state.products.orderProductsCursorIndex,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderList);



// WEBPACK FOOTER //
// ./src/view/order/OrderList.tsx